import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Activity,
  GitBranch,
  LayoutGrid,
  ShieldCheck,
  TrendingUp,
  UserCog,
  Users
} from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import StatPill from '../components/ui/StatPill';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import MetricTile from '../components/admin/MetricTile';
import TeamDirectory from '../components/admin/TeamDirectory';
import ProvisionAccountForm from '../components/admin/ProvisionAccountForm';
import { useAuth } from '../hooks/useAuth';
import { getAdminOverview, getVersioningOverview } from '../utils/api';
import { relativeTime } from '../utils/commandCenterHelpers';

export default function CommandCenterPage() {
  const { user } = useAuth();

  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [versioning, setVersioning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, versioningOverview] = await Promise.all([
        getAdminOverview(),
        getVersioningOverview()
      ]);
      setMetrics(overview.data.metrics);
      setUsers(overview.data.users);
      setVersioning(versioningOverview.data.summary);
      setLoadedAt(new Date());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load Command Center');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const entitiesWithRevisions = versioning?.entitiesWithHistory ?? 0;

  return (
    <>
      <PageHeader
          eyebrow="Super Admin"
          title="Executive command center"
          subtitle="Oversee your team, audit every save, and govern data versions across the operation."
          icon={ShieldCheck}
          pills={[
            <StatPill key="me" icon={UserCog} label="Signed in" value={`@${user?.username || 'admin'}`} />,
            <StatPill
              key="rev"
              icon={GitBranch}
              label="Entities w/ revisions"
              value={entitiesWithRevisions}
            />
          ]}
        />

        {/* metric tiles */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <MetricTile
            icon={Users}
            label="Workforce"
            value={loading ? '—' : metrics?.totalUsers ?? 0}
            accent="brand"
            hint={metrics ? `${metrics.viewers} viewers` : null}
          />
          <MetricTile
            icon={ShieldCheck}
            label="Admins"
            value={loading ? '—' : metrics?.admins ?? 0}
            accent="violet"
            hint={metrics ? `${metrics.superAdmins} super admin${metrics.superAdmins === 1 ? '' : 's'}` : null}
          />
          <MetricTile
            icon={Activity}
            label="Tracked changes"
            value={loading ? '—' : metrics?.totalChanges ?? 0}
            accent="amber"
          />
          <MetricTile
            icon={TrendingUp}
            label="Active contributors"
            value={loading ? '—' : metrics?.activeContributors ?? 0}
            accent="emerald"
          />
        </div>

        {/* two-column grid */}
        <div className="mt-5 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start">
          <TeamDirectory users={users} loading={loading} onChanged={load} />
          <ProvisionAccountForm onCreated={load} />
        </div>

        {/* footer snapshot bar */}
        <div className="mt-6 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-soft">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
            <LayoutGrid className="h-3.5 w-3.5 text-slate-400" />
            Snapshot
          </span>
          <Badge variant="brand" dot>
            {metrics?.totalUsers ?? 0} live accounts
          </Badge>
          <Badge variant="warning" dot>
            {entitiesWithRevisions} entities with revisions
          </Badge>
          <Badge variant="success" dot>
            {metrics?.superAdmins ?? 0} super admins
          </Badge>
          <span className="ml-auto text-[11.5px] text-slate-400">
            Updated {loadedAt ? relativeTime(loadedAt) : '—'}
          </span>
          <Button size="xs" variant="ghost" onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
    </>
  );
}
